import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * What corner state `SessionDrawer` is currently painting, published so layouts
 * can react to it (custom properties can't cross the fixed-drawer boundary, and
 * it's derived from state private to `SessionDrawer`).
 *
 * - `'off'`    — nothing painted.
 * - `'prompt'` — the proactive „Pornește instanța" offer, with NO active session.
 *                Slimmed to a compact corner FAB that does NOT cover the results
 *                grid's STATUS column, so layouts must NOT reserve a gutter for it.
 * - `'drawer'` — a real session drawer (provisioning/ready/failed): the full
 *                ~352px card that DOES overlap a wide grid, so layouts reserve a
 *                right gutter only in this phase.
 * - `'docked'` — a live session whose drawer has been minimized into `DocTop`'s
 *                opening line (2026-08-21 UI report). The corner is EMPTY, so
 *                layouts must not reserve a gutter — the `=== 'drawer'` checks
 *                that guard the gutter already fall through correctly.
 *
 * `ResultsTable`'s `pr-[23rem]` gutter gates on `phase === 'drawer'` (via
 * `useSessionDrawerPhase`) — that's the fix for the prompt-phase width squeeze.
 */
export type SessionDrawerPhase = 'off' | 'prompt' | 'drawer' | 'docked';

interface Presence {
  phase: SessionDrawerPhase;
  setPhase: (p: SessionDrawerPhase) => void;
  /**
   * Whether the operator (or `useAutoDockOnInteraction`) has minimized the
   * drawer into `DocTop`'s chip. Owned HERE rather than inside `SessionDrawer`
   * because both ends need it: the drawer to stop painting the corner card,
   * and `SessionChip` — mounted in a completely different subtree — to paint
   * itself and to hand control back on click.
   */
  docked: boolean;
  setDocked: (v: boolean) => void;
}

const Ctx = createContext<Presence | null>(null);
const NOOP = () => {};

export function SessionDrawerPresenceProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<SessionDrawerPhase>('off');
  const [docked, setDocked] = useState(false);
  const value = useMemo(() => ({ phase, setPhase, docked, setDocked }), [phase, docked]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Docked flag + its setter. `[false, noop]` outside the provider (isolated tests). */
export function useSessionDocked(): [boolean, (v: boolean) => void] {
  const ctx = useContext(Ctx);
  return [ctx?.docked ?? false, ctx?.setDocked ?? NOOP];
}

/** The full phase. `'off'` outside the provider (isolated component tests). */
export function useSessionDrawerPhase(): SessionDrawerPhase {
  return useContext(Ctx)?.phase ?? 'off';
}

/** True whenever the drawer paints ANY corner state. Kept for consumers that
 *  only need presence, not which phase. */
export function useSessionDrawerOccupiesCorner(): boolean {
  return useSessionDrawerPhase() !== 'off';
}

/** Publisher side (`SessionDrawer` only): stable across renders, safe as a
 *  `useEffect` dependency; a no-op outside the provider. */
export function useSetSessionDrawerPhase(): (p: SessionDrawerPhase) => void {
  return useContext(Ctx)?.setPhase ?? NOOP;
}

/**
 * Deprecated boolean-setter compat, retained so the shared `renderResultsTable`
 * harness keeps compiling while the gutter test migrates to `phase`. Maps
 * `true` → `'drawer'` (the phase whose footprint the gutter guards against),
 * `false` → `'off'`.
 */
export function useSetSessionDrawerOccupiesCorner(): (v: boolean) => void {
  const setPhase = useSetSessionDrawerPhase();
  return useCallback((v: boolean) => setPhase(v ? 'drawer' : 'off'), [setPhase]);
}
